import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';

import { LatestAiAdvisory } from '../../core/df/df.types';
import { AiMarkComponent } from '../ai-mark/ai-mark';
import { IconComponent } from '../icon/icon';
import { StatusChipComponent } from '../status-chip/status-chip';

/**
 * AI advisory panel (Ops-only surface; N4).
 *
 * Shared by both product review pages so the advisory reads identically on KS
 * and DF. It surfaces the *full* persisted advisory — recommendation, summary,
 * confidence, risk flags and missing items — not just the enum mirror, so the
 * reviewer can see why the AI reached its recommendation and justify their own
 * decision. The recommendation is advisory only; the human decision governs.
 *
 * Dumb component: the host owns the assessment call and passes `advisory`,
 * `running`, and an optional `committeeCleared` (DF) to reconcile a non-approve
 * advisory against a passed Credit Committee resolution.
 */
@Component({
  selector: 'app-ai-advisory-panel',
  imports: [
    DatePipe,
    DecimalPipe,
    MatCardModule,
    MatButtonModule,
    AiMarkComponent,
    IconComponent,
    StatusChipComponent,
  ],
  templateUrl: './ai-advisory-panel.html',
  styleUrl: './ai-advisory-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAdvisoryPanelComponent {
  /** Full advisory, or null when no assessment has been run. */
  readonly advisory = input<LatestAiAdvisory | null>(null);
  /** Whether an assessment is in flight. */
  readonly running = input(false);
  /**
   * When true, a passed Credit Committee resolution exists for this matter, so a
   * non-approve advisory is reconciled with a note (the committee governs).
   */
  readonly committeeCleared = input(false);
  /**
   * Whether the model's confidence number is shown. The Kickstart Ops page
   * passes false: Ops sees the band, never the score (DOC-54 §5, FLOW-347).
   */
  readonly showConfidence = input(true);
  /** False where the back end refuses a run now: the button is not offered. */
  readonly canRun = input(true);
  /**
   * The press's label in both states, where the host names it. Kickstart Ops
   * passes *Run the assessment* (`FC-DET-112`, DOC-89 §6); DF keeps its own.
   */
  readonly runLabel = input('');
  /** Why the run is not offered, shown in its place (DOC-92 UI-DA-92-05). */
  readonly runWait = input('');

  /** Host runs (or re-runs) the assessment. */
  readonly assess = output<void>();

  protected readonly hasAdvisory = computed(() => this.advisory() !== null);

  /** Show the reconciliation note: committee cleared but advisory isn't approve. */
  protected readonly showContradiction = computed(
    () => this.committeeCleared() && this.advisory()?.recommendation !== 'APPROVE',
  );

  /** Per-model rows, only when more than one model actually ran. */
  protected readonly perModel = computed(() => {
    const rows = this.advisory()?.per_model ?? [];
    return rows.length > 1 ? rows : [];
  });

  /** Whether to render the model-comparison sub-panel. */
  protected readonly showComparison = computed(() => this.perModel().length > 0);

  /** Did every model agree? (drives the badge text/icon, not just colour). */
  protected readonly agreed = computed(() => this.advisory()?.agreement === true);

  /**
   * Cost spread across the models that ran: the cheapest vs the dearest, so Ops
   * can see what the extra models cost. `null` when there's nothing to compare.
   */
  protected readonly costSpread = computed(() => {
    const rows = this.perModel();
    if (rows.length < 2) return null;
    const zar = rows.map((r) => Number(r.cost_zar)).filter((n) => !Number.isNaN(n));
    if (zar.length < 2) return null;
    const min = Math.min(...zar);
    const max = Math.max(...zar);
    const factor = min > 0 ? max / min : null;
    return { min, max, delta: max - min, factor };
  });
}
